import { z } from "zod";
import { CURRENT_POSITION_ELIGIBLE_CLASSES, EPISTEMIC_CLASSES, EPISTEMIC_DISCIPLINE, checkPositionSentence, renderEntrySentence, renderEpistemicSections, selectPublishedEpistemicEntries, } from "./epistemic-selection.js";
import { CURRENT_POSITION_TARGET_SENTENCES, CurrentPositionItemSchema, CurrentPositionOriginSchema, CurrentPositionStatusSchema, keepGroundedCurrentPositionItems, } from "./subject-current-position.js";
import { SUBJECT_INTELLIGENCE_WINDOW_DAYS } from "./subject-intelligence.js";
/** Bump when the Portal CP prompt or skeleton shape changes. */
export const PORTAL_CP_SYNTHESIS_VERSION = 2;
export const PORTAL_CURRENT_POSITION_DISCIPLINE = [
    "CLIENT CURRENT POSITION DISCIPLINE:",
    "You synthesise a Current Position for a Latitude30 client programme.",
    "The PUBLISHED CONTEXT below is the complete authorized corpus for this client engagement.",
    "Do not infer unpublished internal knowledge. Do not imply that hidden programme material exists.",
    "Do not mention internal staff views, Attention, open questions, or operational associations.",
    "Knowledge (published facts) is distinct from evidence (published excerpts). Do not treat evidence as a full document unless a document grant is listed.",
    "Write 3–5 concise sentences answering: where does this programme currently stand, based only on information published to this client, keeping each published record at exactly the strength of its epistemic class?",
    "Publication of a record to this client does NOT make it agreed, adopted, verified or a counterparty's own statement. Only records under ORGANISATION POSITION, AGREED, VERIFIED OBSERVATIONS or COUNTERPARTY POSITIONS may be written as present position; describe every other record as what the record states.",
    "Do not fabricate completeness. Prefer current published values. Name a connected origin only when that origin appears in the supplied facts.",
    "Each sentence MUST include citationIds drawn only from the supplied [citationId] tokens.",
    "Do not invent citations. Do not state that unpublished information exists.",
    'Return JSON only: {"sentences":[{"text":"...","citationIds":["..."]}]}',
].join(" ");
export const PortalCurrentPositionResultSchema = z.object({
    status: CurrentPositionStatusSchema,
    origin: CurrentPositionOriginSchema,
    generatedAt: z.string().datetime(),
    subjectId: z.string().uuid(),
    truncated: z.boolean(),
    items: z.array(CurrentPositionItemSchema),
    insufficientReason: z.string().nullable(),
});
export const PortalUpdateKindSchema = z.enum([
    "published_value_changed",
    "newly_published",
    "document_granted",
]);
export const PortalUpdateItemSchema = z.object({
    id: z.string().min(1).max(80),
    kind: PortalUpdateKindSchema,
    at: z.string().datetime(),
    summary: z.string().min(1).max(400),
    citationId: z.string().min(1).max(800).nullable(),
});
export const PortalUpdatesResponseSchema = z.object({
    window: z.object({
        from: z.string().datetime(),
        to: z.string().datetime(),
        days: z.number().int().positive(),
    }),
    updates: z.array(PortalUpdateItemSchema),
});
export const PORTAL_UPDATES_LIMIT = 8;
function fnv1aHex(input) {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
function clip(text, max) {
    const trimmed = text.trim();
    if (trimmed.length <= max)
        return trimmed;
    return `${trimmed.slice(0, Math.max(0, max - 1))}…`;
}
function factLabel(key) {
    return key.replaceAll("_", " ");
}
export function collectPublishedCitationIds(published) {
    const ids = new Set();
    for (const fact of published.facts)
        ids.add(fact.citationId);
    for (const row of published.evidence)
        ids.add(row.citationId);
    return ids;
}
export function publishedSubjectContentIdentity(published) {
    const payload = JSON.stringify({
        synthesisVersion: PORTAL_CP_SYNTHESIS_VERSION,
        engagementId: published.publication.engagementId,
        subjectId: published.subjectId,
        truncated: published.publication.truncated,
        facts: published.facts.map((row) => ({
            originSubjectId: row.originSubjectId,
            key: row.key,
            value: row.value,
            unit: row.unit,
            asOf: row.asOf,
            citationId: row.citationId,
            recordedAt: row.recordedAt,
            epistemic: row.epistemic ?? null,
        })),
        evidence: published.evidence.map((row) => ({
            citationId: row.citationId,
            title: row.title,
            excerpt: row.excerpt,
        })),
        documents: published.documents.map((row) => ({
            grantId: row.grantId,
            title: row.title,
        })),
    });
    return `portalcp:${PORTAL_CP_SYNTHESIS_VERSION}:${fnv1aHex(payload)}:${fnv1aHex(payload.slice(Math.floor(payload.length / 2)))}`;
}
export function portalCurrentPositionCacheKey(tenantSlug, engagementId, subjectId) {
    return `portalcp:${tenantSlug}:${engagementId}:${subjectId}`;
}
export function selectPublishedEpistemic(published) {
    return selectPublishedEpistemicEntries(published.facts, published.label);
}
export const PORTAL_CURRENT_POSITION_INSUFFICIENT_UNCLASSIFIED = "No adopted, agreed or verified position has been published. The published records below are shown as recorded, not as an agreed position.";
/**
 * Sufficient only when an ELIGIBLE published position exists (ADR-0117).
 * Until the Organisation B publisher attaches epistemic projections, every
 * published fact is unclassified legacy: listed as recorded, never as a
 * position.
 */
export function assessPublishedCurrentPositionSufficiency(published, selection = selectPublishedEpistemic(published)) {
    if (selection.eligible.length > 0)
        return { status: "sufficient", reason: null };
    if (published.facts.length > 0 || published.evidence.length > 0) {
        return { status: "insufficient", reason: PORTAL_CURRENT_POSITION_INSUFFICIENT_UNCLASSIFIED };
    }
    return {
        status: "insufficient",
        reason: "No published knowledge is available for a present position.",
    };
}
export function buildPublishedCurrentPositionSkeleton(published, selection = selectPublishedEpistemic(published)) {
    const sufficiency = assessPublishedCurrentPositionSufficiency(published, selection);
    if (published.facts.length === 0 && published.evidence.length === 0) {
        return [
            {
                text: sufficiency.reason ?? "No published knowledge is available for a present position.",
                citationIds: [],
            },
        ];
    }
    // The insufficiency reason travels in `insufficientReason`; items carry
    // the framed published records so clients keep seeing what was published.
    const items = [];
    const ordered = [
        ...selection.entries.filter((entry) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(entry.class)),
        ...selection.entries.filter((entry) => !CURRENT_POSITION_ELIGIBLE_CLASSES.has(entry.class)),
    ];
    for (const entry of ordered) {
        if (items.length >= CURRENT_POSITION_TARGET_SENTENCES)
            break;
        items.push({ text: renderEntrySentence(entry), citationIds: [...entry.citationIds] });
    }
    return items.slice(0, CURRENT_POSITION_TARGET_SENTENCES);
}
export function projectPublishedSubjectContextForCurrentPosition(published, selection = selectPublishedEpistemic(published)) {
    const eligibleHeading = "## ELIGIBLE PUBLISHED POSITION (deterministically selected)";
    const eligible = renderEpistemicSections(selection, {
        classes: EPISTEMIC_CLASSES.filter((cls) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(cls)),
        heading: eligibleHeading,
    }) ||
        `${eligibleHeading}\nNone. Do not write any sentence as an agreed, adopted or verified position.`;
    const other = renderEpistemicSections(selection, {
        classes: EPISTEMIC_CLASSES.filter((cls) => !CURRENT_POSITION_ELIGIBLE_CLASSES.has(cls)),
        heading: "## OTHER PUBLISHED RECORDS (keep each record's frame; never a position)",
    }) || "No other published facts.";
    const evidenceLines = published.evidence.length === 0
        ? "No published evidence."
        : published.evidence
            .map((row) => `PUBLISHED EVIDENCE ${row.typeLabel}: [${row.citationId}] ${row.title}${row.excerpt ? ` — ${row.excerpt}` : ""}`)
            .join("\n");
    const documentLines = published.documents.length === 0
        ? "No granted source documents."
        : published.documents.map((row) => `GRANTED DOCUMENT ${row.title}`).join("\n");
    return [
        PORTAL_CURRENT_POSITION_DISCIPLINE,
        EPISTEMIC_DISCIPLINE,
        `Programme: ${published.label} (${published.kind})`,
        "## PUBLISHED CONTEXT (complete authorized corpus)",
        eligible,
        other,
        evidenceLines,
        documentLines,
    ].join("\n\n");
}
export function composePortalCurrentPositionResult(input) {
    const sufficiency = assessPublishedCurrentPositionSufficiency(input.published);
    const items = sufficiency.status === "insufficient"
        ? input.items
        : input.items.filter((item) => item.citationIds.length > 0);
    return PortalCurrentPositionResultSchema.parse({
        status: sufficiency.status,
        origin: input.origin,
        generatedAt: input.generatedAt ?? new Date().toISOString(),
        subjectId: input.published.subjectId,
        truncated: input.published.publication.truncated,
        items,
        insufficientReason: sufficiency.reason,
    });
}
export function groundPortalCurrentPositionItems(items, published) {
    const allowed = collectPublishedCitationIds(published);
    const selection = selectPublishedEpistemic(published);
    return keepGroundedCurrentPositionItems(items, allowed)
        .filter((item) => checkPositionSentence(item.text, item.citationIds, selection).ok)
        .slice(0, CURRENT_POSITION_TARGET_SENTENCES);
}
function toIso(value) {
    if (value instanceof Date)
        return value.toISOString();
    return value;
}
function inWindow(at, fromMs) {
    const ms = Date.parse(at);
    return Number.isFinite(ms) && ms >= fromMs;
}
function updateId(parts) {
    return `upd:${fnv1aHex(parts.join("|"))}`;
}
function factByKey(published, factKey) {
    return published.facts.find((row) => row.key === factKey);
}
function factByCitation(published, citationId) {
    return published.facts.find((row) => row.citationId === citationId);
}
/**
 * Derive client Updates from the published assembler output plus publication
 * events. Unpublished corpus never enters this function.
 */
export function derivePublishedUpdates(input) {
    const now = input.now ?? new Date();
    const days = input.windowDays ?? SUBJECT_INTELLIGENCE_WINDOW_DAYS;
    const fromMs = now.getTime() - days * 24 * 60 * 60 * 1000;
    const from = new Date(fromMs).toISOString();
    const to = now.toISOString();
    const allowed = collectPublishedCitationIds(input.published);
    const updates = [];
    const seen = new Set();
    const push = (item) => {
        if (seen.has(item.id))
            return;
        if (item.citationId && !allowed.has(item.citationId) && item.kind !== "document_granted") {
            return;
        }
        seen.add(item.id);
        updates.push(item);
    };
    for (const fact of input.published.facts) {
        if (!inWindow(fact.recordedAt, fromMs))
            continue;
        push({
            id: updateId(["value", fact.citationId, fact.recordedAt]),
            kind: "published_value_changed",
            at: fact.recordedAt,
            summary: `Published ${factLabel(fact.key)} is now ${fact.value}${fact.unit ? ` ${fact.unit}` : ""}.`,
            citationId: fact.citationId,
        });
    }
    for (const event of input.events) {
        if (event.action !== "item_grant")
            continue;
        const at = toIso(event.at);
        if (!inWindow(at, fromMs))
            continue;
        const payload = event.payload;
        const factKey = typeof payload["factKey"] === "string" ? payload["factKey"] : null;
        const citationId = typeof payload["citationId"] === "string" ? payload["citationId"] : null;
        const current = (factKey ? factByKey(input.published, factKey) : undefined) ??
            (citationId ? factByCitation(input.published, citationId) : undefined);
        const evidence = citationId && input.published.evidence.find((row) => row.citationId === citationId);
        if (current) {
            const alreadyValue = updates.some((row) => row.kind === "published_value_changed" && row.citationId === current.citationId);
            if (alreadyValue)
                continue;
            push({
                id: updateId(["grant", current.citationId, at]),
                kind: "newly_published",
                at,
                summary: `Additional published knowledge is now available on this programme.`,
                citationId: current.citationId,
            });
            continue;
        }
        if (evidence) {
            push({
                id: updateId(["grant", evidence.citationId, at]),
                kind: "newly_published",
                at,
                summary: `Additional published evidence is now available on this programme.`,
                citationId: evidence.citationId,
            });
        }
    }
    for (const grant of input.documentGrants ?? []) {
        const at = toIso(grant.grantedAt);
        if (!inWindow(at, fromMs))
            continue;
        if (!input.published.documents.some((row) => row.grantId === grant.grantId))
            continue;
        push({
            id: updateId(["doc", grant.grantId, at]),
            kind: "document_granted",
            at,
            summary: `A source document is now available: ${clip(grant.title, 120)}.`,
            citationId: null,
        });
    }
    updates.sort((a, b) => b.at.localeCompare(a.at));
    return PortalUpdatesResponseSchema.parse({
        window: { from, to, days },
        updates: updates.slice(0, PORTAL_UPDATES_LIMIT),
    });
}
//# sourceMappingURL=portal-subject-current-position.js.map